import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface ExternalEvent {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  calendar_name: string;
  color: string;
  is_all_day: boolean;
}

interface Conflict {
  event1: { id: string; title: string; type: string };
  event2: { id: string; title: string; type: string };
  overlapStart: string;
  overlapEnd: string;
}

const CalendarSync: React.FC = () => {
  const [providers, setProviders] = useState<any>({});
  const [externalEvents, setExternalEvents] = useState<ExternalEvent[]>([]);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, eventsRes] = await Promise.all([
        apiService.getCalendarStatus(),
        apiService.getCalendarEvents(dateRange.startDate, dateRange.endDate)
      ]);
      if (statusRes.success) setProviders(statusRes.providers || {});
      if (eventsRes.success) {
        setExternalEvents(eventsRes.externalEvents || []);
        setConflicts(eventsRes.conflicts || []);
      }
    } catch (error) {
      console.error('Failed to fetch calendar status:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await apiService.syncCalendar();
      if (response.success) {
        alert(response.message);
        fetchStatus();
      } else if (response.setupRequired) {
        alert(response.message);
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading calendar data...</div>;
  }

  return (
    <div style={{ padding: '24px' }} data-testid="calendar-sync">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>Multi-Calendar Sync</h2>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>Merge external calendars with conflict detection</p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          style={{
            padding: '10px 20px',
            backgroundColor: syncing ? '#9ca3af' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            cursor: syncing ? 'not-allowed' : 'pointer'
          }}
        >
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>

      {/* Connected Calendars */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', fontWeight: 600, color: '#374151' }}>Connected Calendars</h3>
        
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          {/* Google Calendar */}
          <div style={{
            padding: '20px',
            backgroundColor: providers.google?.connected ? '#f0fdf4' : '#f9fafb',
            borderRadius: '12px',
            border: providers.google?.connected ? '2px solid #22c55e' : '1px solid #e5e7eb',
            minWidth: '200px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '2rem' }}>📅</span>
              <div>
                <p style={{ margin: 0, fontWeight: 600, color: '#374151' }}>Google Calendar</p>
                <p style={{ margin: 0, fontSize: '0.8rem', color: providers.google?.connected ? '#22c55e' : '#6b7280' }}>
                  {providers.google?.connected ? 'Connected' : 'Not connected'}
                </p>
              </div>
            </div>
            
            {providers.google?.connected ? (
              <div>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#374151' }}>
                  {providers.google?.calendars?.length || 0} calendar(s) synced
                </p>
                <button
                  onClick={() => apiService.disconnectCalendar('google').then(fetchStatus)}
                  style={{ padding: '6px 12px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer' }}
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <div>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.8rem', color: '#6b7280' }}>
                  OAuth setup required by admin
                </p>
                <button
                  onClick={() => alert('Google Calendar OAuth requires admin configuration. Please contact your administrator.')}
                  style={{ padding: '8px 16px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  Connect
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0f4ff', borderRadius: '8px' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#4338ca' }}>
            <strong>Note:</strong> To connect Google Calendar, your administrator needs to set up OAuth credentials in Google Cloud Console. 
            Once configured, you&apos;ll be able to link your account with one click.
          </p>
        </div>
      </div>

      {/* Date Range Selector */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', alignItems: 'center' }}>
        <span style={{ color: '#374151', fontWeight: 500 }}>View events from:</span>
        <input
          type="date"
          value={dateRange.startDate}
          onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
        />
        <span style={{ color: '#6b7280' }}>to</span>
        <input
          type="date"
          value={dateRange.endDate}
          onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
        />
      </div>

      {/* Conflicts Warning */}
      {conflicts.length > 0 && (
        <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px' }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px' }}>
            ⚠️ Schedule Conflicts Detected
          </h4>
          {conflicts.map((conflict, idx) => (
            <div key={idx} style={{ padding: '8px 12px', backgroundColor: '#fee2e2', borderRadius: '6px', marginBottom: '8px' }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#991b1b' }}>
                <strong>{conflict.event1.title}</strong> overlaps with <strong>{conflict.event2.title}</strong>
              </p>
            </div>
          ))}
        </div>
      )}

      {/* External Events */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#374151' }}>External Calendar Events</h3>
        </div>
        
        {externalEvents.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ color: '#6b7280', marginBottom: '16px' }}>No external events synced yet</p>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Connect a calendar above to see your events here</p>
          </div>
        ) : (
          <div style={{ padding: '16px' }}>
            {externalEvents.map(event => (
              <div
                key={event.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderLeft: `4px solid ${event.color || '#3b82f6'}`,
                  backgroundColor: '#f9fafb',
                  borderRadius: '0 8px 8px 0',
                  marginBottom: '8px'
                }}
              >
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 600, color: '#374151' }}>{event.title}</p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                    {formatDate(event.start_time)} • {event.is_all_day ? 'All day' : `${formatTime(event.start_time)} - ${formatTime(event.end_time)}`}
                  </p>
                </div>
                <span style={{ padding: '4px 10px', backgroundColor: `${event.color}20`, color: event.color || '#3b82f6', borderRadius: '12px', fontSize: '0.75rem' }}>
                  {event.calendar_name}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarSync;
